import {
  Button,
  Column,
  Flex,
  Row,
  Stack,
} from '@grubhubprod/gh-cookbook-react/dist';
import classnames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { AccountPaymentForm } from 'src/components/Account/AccountPaymentForm';
import { SimpleDialog } from 'src/components/SimpleDialog/SimpleDialog';
import { useAnalytics } from 'src/components/AnalyticsProvider/AnalyticsProvider';
import { PageSectionTitle } from 'src/components/selfService/layout/PageSectionTitle/PageSectionTitle';
import {
  LineOfCreditSearchType,
  useLineOfCreditSearch,
} from 'src/utilities/useSearchHooks';
import { ICreditCard } from 'src/types/CreditCard';
import { useScreenSizes } from 'src/utilities/useScreenSizes';
import { sdk } from 'src/utilities/sdk';
import {
  pushError,
  pushErrorFromAPI,
  pushSuccess,
} from 'src/utilities/notifications';

export type CreditCardPaymentSectionProps = {
  corpId: string;
  isInternalAdminUser: boolean;
};

export function CreditCardPaymentSection({
  corpId,
  isInternalAdminUser,
}: CreditCardPaymentSectionProps): JSX.Element {
  const { isPhone } = useScreenSizes();
  const { push } = useHistory();
  const { sendTealiumEvent } = useAnalytics();

  const [card, setCard] = useState<ICreditCard>(null);
  const [showUpdatePaymentForm, setShowUpdatePaymentForm] = useState(false);
  const [showRemoveCardModal, setShowRemoveCardModal] = useState(false);
  const [showViewLinesOfCreditModal, setShowViewLinesOfCreditModal] =
    useState(false);

  const { totalResults: totalLinesOfCredit, doSearch: doSearchLineOfCredit } =
    useLineOfCreditSearch(corpId);

  useEffect(() => {
    if (corpId) {
      getCreditCard();
      verifyLocExistence();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corpId]);

  async function verifyLocExistence() {
    try {
      await doSearchLineOfCredit({ filter_by: LineOfCreditSearchType.ACTIVE });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(e);
    }
  }

  async function getCreditCard() {
    try {
      let url = `/payments/corporate/${corpId}/credit_card`;
      if (isInternalAdminUser) {
        url = `${url}/internal_admin`;
      }
      const [fetchedCard] = await sdk.http.get(url);
      setCard(fetchedCard);
    } catch (error) {
      pushErrorFromAPI(error);
    }
  }

  function getCardInfo() {
    return `•••• •••• •••• ${card.credit_card_last4}, exp ${card.expiration_year}`;
  }

  function checkBeforeRemovingCard() {
    sendTealiumEvent({
      eventcategory: 'self-service management tool_your account',
      eventaction: 'delete credit card_cta',
    });
    if (totalLinesOfCredit > 0) {
      setShowViewLinesOfCreditModal(true);
      sendTealiumEvent({
        eventcategory: 'self-service management tool_your account',
        eventaction: 'view active line of credit_impression',
      });
    } else {
      setShowRemoveCardModal(true);
      sendTealiumEvent({
        eventcategory: 'self-service management tool_your account',
        eventaction: 'remove credit card_impression',
      });
    }
  }

  async function onRemoveCard() {
    await removeCard();
    setShowRemoveCardModal(false);
  }

  async function removeCard() {
    try {
      await sdk.http.delete(
        `/payments/corporate/${corpId}/payments/${card.id}`
      );

      pushSuccess('Your card has been removed.');
      sendTealiumEvent({
        eventcategory: 'self-service management tool_your account',
        eventaction: 'remove credit card_cta',
        eventlabel: 'success',
      });
      setCard(null);
    } catch (e) {
      pushError('There was a problem removing your card. Try again.');
      sendTealiumEvent({
        eventcategory: 'self-service management tool_your account',
        eventaction: 'remove credit card_cta',
        eventlabel: 'error',
      });
    }
  }

  async function onUpdateCard() {
    await getCreditCard();
    setShowUpdatePaymentForm(false);
    pushSuccess('Your card has been updated.');
  }

  function onCardSubmissionError() {
    setShowUpdatePaymentForm(false);
    pushError('There was a problem updating your card. Try again.');
  }

  function goToLocPage() {
    sendTealiumEvent({
      eventcategory: 'self-service management tool_your account',
      eventaction: 'view active line of credit_cta',
    });
    push(`/corporate/management/${corpId}/lineOfCredit`);
  }

  return (
    <>
      <Row gutter={0}>
        <Column gutter={0} phone={12} tablet={showUpdatePaymentForm ? 12 : 9}>
          <Stack x={isPhone ? 'none' : 'sm'} y={isPhone ? 'sm' : 'none'}>
            <PageSectionTitle title="Payment" y="x-sm" headingVariant="h5" />
            <Stack x={isPhone ? 'none' : 'md'} y="sm">
              {card ? (
                <span>
                  This card will be charged every time someone places an order
                  with a line of credit.
                </span>
              ) : (
                <span>Add a credit card to create a line of credit.</span>
              )}
            </Stack>
            {!showUpdatePaymentForm ? (
              <>
                {card && (
                  <>
                    <Flex alignItems="center">
                      <Stack
                        x="x-sm"
                        className={classnames('creditCardIcon', {
                          [card.credit_card_type]: true,
                        })}
                      />
                      {getCardInfo()}
                    </Flex>
                    {!isInternalAdminUser && (
                      <Button
                        variant="tertiary"
                        className="removeButtonPadding"
                        buttonSize="medium"
                        onClick={checkBeforeRemovingCard}
                      >
                        Remove
                      </Button>
                    )}
                  </>
                )}
              </>
            ) : (
              <AccountPaymentForm
                cardId={card ? card.id : null}
                corpId={corpId}
                onClose={() => setShowUpdatePaymentForm(false)}
                onSuccess={onUpdateCard}
                onError={onCardSubmissionError}
                isPhone={isPhone}
              />
            )}
          </Stack>
        </Column>
        {!showUpdatePaymentForm && !isInternalAdminUser && (
          <Column gutter={0} phone={12} tablet={3}>
            <Flex alignItems="center" height="100%">
              <Button
                variant="secondary"
                buttonSize="medium"
                block
                onClick={() => setShowUpdatePaymentForm(true)}
              >
                {card ? 'Update payment' : 'Add credit card'}
              </Button>
            </Flex>
          </Column>
        )}
      </Row>

      {showRemoveCardModal && (
        <SimpleDialog
          primaryActionFlavor="danger"
          title="Remove this card?"
          description="This card will be removed from your account. Don't forget: you'll need to add a new credit card before you can create a line of credit. "
          primaryActionText="Remove"
          buttonsDirection="row-reverse"
          secondaryActionText="Cancel"
          onSecondaryActionClick={() => setShowRemoveCardModal(false)}
          onPrimaryActionClick={onRemoveCard}
        />
      )}

      {showViewLinesOfCreditModal && (
        <SimpleDialog
          title="We can't remove this card just yet"
          description="Before you can remove this card, you must delete all lines of credit."
          primaryActionText="View lines of credit"
          buttonsDirection="column"
          secondaryActionText="Cancel"
          onSecondaryActionClick={() => setShowViewLinesOfCreditModal(false)}
          onPrimaryActionClick={goToLocPage}
        />
      )}
    </>
  );
}
